import { withRouter } from 'react-router-dom';
import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { hasConversationsInteract as hasConversationsInteractSelector } from '../../auth/selectors/hasConversationsInteract';
import { getEditorDisabledMessage } from '../../message-composer/operators/getEditorDisabledMessage';
import { setFragment } from '../../routing/actions/setFragment';
import { getFragmentFromLocation } from '../../routing/operators/getFragmentFromLocation';
import { useIsBulkUpdated } from 'find-and-filter-data/bulk-operations/public';
import { getIsAdminNotMemberOfInbox } from '../../unified-inbox/selectors/getIsAdminNotMemberOfInbox';
import { ResizableReplyEditor } from 'svh-workspace-ui-lib/resizable-thread-view/public/components';
import ErrorBoundary from 'conversations-error-reporting/error-boundaries/components/ErrorBoundary';
import { trackEvent } from '../../usage-tracking/clients/usageTracker';
import { STANDALONE } from 'composer-ui/tabbed-editor/public/constants';
import AIOnboardingContext from 'content-assistance-lib/contexts/onboardingContext';
import { useAIOnboardingPopoverState } from '../hooks/useAIOnboardingPopoverState';
import { getStandaloneConnectionId } from 'calling-cross-tab-library/appData/operators/widgetDetailsOperators';
import { useWidgetDetails } from 'calling-cross-tab-library/external/useWidgetDetails';
import { sendCrossTabMessage } from 'calling-cross-tab-library/external/sendCrossTabMessage';
import { createSetThreadIdMessage } from 'calling-lifecycle-messages/deviceEventCreators';
import { useOptimisticUpdateThreadListMember } from 'find-and-filter-data/view-members-data/public';
import { useCurrentView } from '../../custom-views/hooks/useCurrentView';
import { getId } from 'find-and-filter-data/views-schema/public';
import { jsx as _jsx } from "react/jsx-runtime";
export const InboxReplyComposerDoNotImportDirectly = ({
  location,
  threadId,
  renderExistingFileSelect,
  renderSubmitButton,
  tracker,
  isKnownVisitor
}) => {
  const dispatch = useDispatch();
  const editorFragment = getFragmentFromLocation(location);
  const hasConversationsInteract = useSelector(hasConversationsInteractSelector);
  const hasThreadBeenBatchUpdated = useIsBulkUpdated(threadId);
  const isAdminNotMemberOfInbox = useSelector(getIsAdminNotMemberOfInbox);
  const {
    widgetDetails
  } = useWidgetDetails();
  const activeConnectionId = getStandaloneConnectionId(widgetDetails);
  useEffect(() => {
    if (activeConnectionId) {
      sendCrossTabMessage(createSetThreadIdMessage({
        threadId,
        destinationConnectionId: activeConnectionId
      })).catch(() => {});
    }
  }, [threadId, activeConnectionId]);
  const handleChannelChange = useCallback(({
    fragment
  }) => {
    dispatch(setFragment({
      fragment,
      replace: true
    }));
  }, [dispatch]);
  const disabledMessage = useMemo(() => getEditorDisabledMessage({
    hasConversationsInteract,
    hasThreadBeenBatchUpdated,
    isAdminNotMemberOfInbox
  }), [hasConversationsInteract, hasThreadBeenBatchUpdated, isAdminNotMemberOfInbox]);
  const AIOnboardingPopoverState = useAIOnboardingPopoverState();
  const currentView = useCurrentView();
  const viewId = currentView ? getId(currentView) : undefined;
  const {
    update
  } = useOptimisticUpdateThreadListMember();
  const handleDraftChange = useCallback(({
    text
  }) => {
    if (!viewId) {
      return;
    }
    update({
      threadId: +threadId,
      viewId,
      viewMemberUpdater: member => {
        if (!member.get('latestMessagePreview')) {
          return member;
        }
        return member.set('latestMessagePreview', Object.assign({}, member.get('latestMessagePreview'), {
          isLatestMessageADraft: text.length > 0
        }, text.length > 0 ? {
          text
        } : {}));
      }
    });
  }, [update, threadId, viewId]);
  return /*#__PURE__*/_jsx(ErrorBoundary, {
    errorBoundaryName: "InboxReplyComposer",
    errorLoggingTitle: "Inbox Reply Composer",
    children: /*#__PURE__*/_jsx(AIOnboardingContext.Provider, {
      value: AIOnboardingPopoverState,
      children: /*#__PURE__*/_jsx(ResizableReplyEditor, {
        allowFullscreen: true,
        disabled: Boolean(disabledMessage),
        disabledMessage: disabledMessage,
        editorFragment: editorFragment,
        isKnownVisitor: isKnownVisitor,
        onChannelChange: handleChannelChange,
        onDraftChange: handleDraftChange,
        renderExistingFileSelect: renderExistingFileSelect,
        renderSubmitButton: renderSubmitButton,
        threadId: threadId,
        tracker: tracker,
        trackEvent: trackEvent,
        callingSource: STANDALONE
      })
    })
  }, "inboxReplyComposer");
};
InboxReplyComposerDoNotImportDirectly.displayName = 'InboxReplyComposer';
// @ts-expect-error ts-migrate(2345) FIXME: Argument of type '{ ({ location, threadId, renderE... Remove this comment to see the full error message
export default withRouter(InboxReplyComposerDoNotImportDirectly);